/**
 * Sandbox pre-mint resolver for the BusinessDemo wizard.
 *
 * Why this exists
 * ----------------
 * The wizard demonstrates several contract templates whose buyer (party
 * A) outflows tokens — `immediate-transfer` / `gift` (single transfer
 * `execute_at: 'now'`), `delivery-with-prepayment-guarantee` (A1
 * prepayment leg `execute_at: 'now'`), `delivery-with-repayment` (A1
 * principal `execute_at: 'now'`), `delivery-post-payment` (A2 payment
 * leg fires at `execute_at = <scheduled>` via TimeKeeper),
 * `scheduled-payment` (single transfer at `execute_at = <scheduled>`).
 *
 * On a freshly-redeployed sandbox the buyer's address has zero balance,
 * so every outflow leg defaults instantly (or eventually, on
 * TimeKeeper) with `INSUFFICIENT_BALANCE` → `agreementDefaulted`. That
 * makes the demo non-self-contained and dependent on out-of-band mints
 * — exactly the regression class observed on `emulator.dlt.ping42.ru`
 * after an unrelated container redeploy wiped the in-memory world.
 *
 * The wizard therefore mints — to addrA, in the same logical step — the
 * exact amount the contract is about to debit from addrA, before
 * issuing `POST /case` / `POST /contract`. Production OP integrations
 * never do this (they fund users via legitimate channels); the helper
 * is sandbox-only and only ships with the emulator UI.
 *
 * Algorithm
 * ----------
 * Walks `spec_full.transfer` (single-leg `/case` shape) and every entry
 * of `spec_full.agreements[]` (composite `/contract` shape), sums the
 * decimal-string `amount` of every leg whose `from === addrA`,
 * regardless of `execute_at` (so scheduled outflows are also covered —
 * a wizard demo that fires its payment leg ten minutes later still
 * needs the buyer pre-funded).
 *
 * Returns the wire-format decimal string (`"100"`, `"100.50"`) for
 * `/admin/mint`'s `amount` field, or `null` when the buyer has zero
 * outflow on this contract (e.g. a hypothetical seller-only template).
 *
 * Determinism + tests
 * --------------------
 * Pure function. No floating-point — uses `BigInt` cents internally for
 * exact arithmetic. Covered by `business-pre-mint.test.ts`.
 */

const AMOUNT_RE = /^(\d+)(?:\.(\d{1,2}))?$/;

function amountToCents(amount: string): bigint {
  const m = AMOUNT_RE.exec(amount);
  if (!m) {
    throw new TypeError(
      `pre-mint: amount must be decimal string with ≤2 fractional digits, got "${amount}"`,
    );
  }
  const [, int, frac = ''] = m;
  return BigInt(int) * 100n + BigInt((frac + '00').slice(0, 2));
}

function centsToAmount(cents: bigint): string {
  if (cents < 0n) {
    throw new TypeError(`pre-mint: cents must be non-negative, got ${cents.toString()}`);
  }
  const int = (cents / 100n).toString();
  const frac = (cents % 100n).toString().padStart(2, '0');
  return frac === '00' ? int : `${int}.${frac}`;
}

interface LegLike {
  readonly from?: unknown;
  readonly amount?: unknown;
}

function collectLegs(specFull: unknown): readonly LegLike[] {
  if (!specFull || typeof specFull !== 'object') return [];
  const sf = specFull as Record<string, unknown>;
  const legs: LegLike[] = [];
  if (sf['transfer'] && typeof sf['transfer'] === 'object') {
    legs.push(sf['transfer'] as LegLike);
  }
  if (Array.isArray(sf['agreements'])) {
    for (const a of sf['agreements']) {
      if (a && typeof a === 'object') legs.push(a as LegLike);
    }
  }
  return legs;
}

/**
 * Compute the total amount addrA outflows on this contract body, as a
 * wire-format decimal string. Returns `null` when addrA has no
 * outflowing leg (no pre-mint needed).
 *
 * Caller is expected to pass the body returned by `buildGatewayBody`
 * — `{ spec_full: ... }`. Anything else returns `null`.
 */
export function computeBuyerPreMint(body: unknown, addrA: string): string | null {
  if (!body || typeof body !== 'object') return null;
  const specFull = (body as Record<string, unknown>)['spec_full'];
  let cents = 0n;
  for (const leg of collectLegs(specFull)) {
    if (typeof leg.from !== 'string' || typeof leg.amount !== 'string') continue;
    if (leg.from.toLowerCase() !== addrA.toLowerCase()) continue;
    cents += amountToCents(leg.amount);
  }
  if (cents === 0n) return null;
  return centsToAmount(cents);
}
